import { useCallback, useMemo, useRef } from 'react'

import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import { getFileMetadata, uploadToDirectUrl } from 'Pods/ThreeDStudio/Hooks/directUpload'
import useRequireLogin from 'Pods/ThreeDStudio/Hooks/useRequireLogin'
import useSourceUrlImageUpload from 'Pods/ThreeDStudio/Hooks/useSourceUrlImageUpload'
import {
  formatMaxFileSizeExceededMessage,
} from 'Pods/ThreeDStudio/Shared/Utils/formatMaxFileSizeExceededMessage'
import { getInputFileUploadConstraints } from 'Pods/ThreeDStudio/Shared/Utils/getInputFileUploadConstraints'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { setIsImageUploading } from 'Pods/ThreeDStudio/Store/actions/ui'
import { ACTION } from 'Pods/ThreeDStudio/Store/constants'

import { formatInvalidImageTypeMessage } from './formatInvalidImageTypeMessage'
import { resizeImageToRequirements } from './imageResize'

export default function useImageUploader(setInputImage, storeSchemaKey) {
  const inputRef = useRef(null)
  const modelSchema = useThreeDStudioStore(state => state.schema[storeSchemaKey])
  const isImageUploading = useThreeDStudioStore(state => state.ui.isImageUploading)
  const { postData } = useApiPost()
  // Uploading an image requires an account; prompt a logged-out user to sign up.
  const requireLogin = useRequireLogin()

  const { allowedTypes, maxFileSize } = useMemo(
    () => getInputFileUploadConstraints(modelSchema, 'input_image'),
    [modelSchema],
  )
  const isRetexture = modelSchema?.action === ACTION.RETEXTURE

  const processFile = useCallback((file) => {
    if (!file || !allowedTypes.includes(file.type)) {
      window.showNotification(
        formatInvalidImageTypeMessage(allowedTypes),
        { type: 'error', autohide: true },
      )

      return Promise.resolve(false)
    }

    if (file.size > maxFileSize) {
      window.showNotification(
        formatMaxFileSizeExceededMessage(maxFileSize),
        { type: 'error', autohide: true },
      )

      return Promise.resolve(false)
    }

    setIsImageUploading(true)

    const createGeneratorAsset = (blob) =>
      new Promise((resolve, reject) => {
        postData({
          path: '/api/internal/generator/assets',
          data: { asset_type: 'image', blob },
          onSuccess: resolve,
          onError: reject,
        })
      })

    return resizeImageToRequirements(file, { forcePng: isRetexture })
      .then(resizedFile => {
        return getFileMetadata(resizedFile)
        .then(blob => createGeneratorAsset(blob))
          .then(async response => {
            const { id, direct_upload: directUpload } = response.data
            if (!directUpload?.url || !directUpload?.headers) {
              throw new Error('Invalid direct upload response')
            }
            await uploadToDirectUrl(directUpload.url, directUpload.headers, resizedFile)
            return { id, resizedFile }
          })
      })
      .then(({ id, resizedFile }) => {
        const newBlobUrl = URL.createObjectURL(resizedFile)
        setInputImage(id, newBlobUrl)
        if (inputRef.current) {
          inputRef.current.value = ''
        }
        return true
      })
      .catch(() => {
        window.showNotification('Upload failed. Please try again.', { type: 'error', autohide: true })
        return false
      })
      .finally(() => {
        setIsImageUploading(false)
      })
  }, [postData, allowedTypes, maxFileSize, isRetexture, setInputImage])

  const onDragOver = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const onDrop = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!requireLogin()) {
      return
    }
    const file = e.dataTransfer?.files?.[0]
    if (file) {
      processFile(file)
    }
  }, [processFile, requireLogin])

  const onFileChange = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    const file = e.target?.files?.[0]
    if (file) {
      processFile(file)
    }
  }, [processFile])

  const onFileClick = useCallback(() => {
    if (!requireLogin()) {
      return
    }
    if (inputRef.current) {
      inputRef.current.click()
    }
  }, [requireLogin])

  const handleRemovePreview = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setInputImage(undefined)
  }, [])

  const handlePasteUrlUpload = useSourceUrlImageUpload(setInputImage)

  return {
    inputRef,
    allowedTypes,
    isLoading: isImageUploading,
    onDragOver,
    onDrop,
    onFileChange,
    onFileClick,
    handleRemovePreview,
    handlePasteUrlUpload,
  }
}
